import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { MailCheck } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

type CheckEmailLocationState = {
  email?: string
}

interface CheckEmailProps extends React.ComponentPropsWithoutRef<"div"> {}

export function CheckEmail({ className, ...props }: CheckEmailProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [email, setEmail] = useState<string | null>(null)

  useEffect(() => {
    const state = location.state as CheckEmailLocationState | null
    const fromState = state?.email?.trim()
    if (!fromState) {
      return
    }

    setEmail(fromState)
    navigate(location.pathname + location.search, { replace: true, state: null })
  }, [location.pathname, location.search, location.state, navigate])

  return (
    <div className={cn("flex flex-col items-center text-center", className)} {...props}>
      <div className="mb-6 flex size-16 items-center justify-center rounded-full bg-success/10 text-success ring-8 ring-success/10">
        <MailCheck className="size-8" aria-hidden />
      </div>

      <div className="space-y-3">
        <h1 className="text-2xl font-semibold leading-tight text-foreground">
          Check your email
        </h1>
        {email ? (
          <p className="text-base text-muted-foreground">
            We sent a verification link to{" "}
            <span className="font-medium text-foreground">{email}</span>.
          </p>
        ) : (
          <p className="text-base text-muted-foreground">
            We sent a verification link to your email address.
          </p>
        )}
        <p className="text-sm leading-6 text-muted-foreground">
          Open the link to verify your email. If this workspace reviews new
          accounts, we will email you again when you can sign in.
        </p>
      </div>

      <div className="mt-8">
        <Button type="button" fullWidth onClick={() => navigate("/login")}>
          Go to sign in
        </Button>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        No email yet? Check your spam folder.
      </p>
    </div>
  )
}
